import { useEffect, useState, useCallback } from "react";
import { Sparkle, X } from "@phosphor-icons/react";
import { useTranslation } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import {
  LATEST_VERSION,
  RELEASES,
  WHATS_NEW_SEEN_KEY,
  OPEN_WHATS_NEW_EVENT,
} from "@/data/releases";

export default function WhatsNewDrawer() {
  const { lang } = useTranslation();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);

  const openDrawer = useCallback(() => setOpen(true), []);

  // Auto-open once per version for signed-in users
  useEffect(() => {
    if (!user || user === false) return;
    const seen = localStorage.getItem(WHATS_NEW_SEEN_KEY);
    if (seen !== LATEST_VERSION) {
      // Small delay so it doesn't compete with page load
      const t = setTimeout(() => setOpen(true), 900);
      return () => clearTimeout(t);
    }
  }, [user]);

  // Listen for programmatic open requests (from navbar button, footer link, etc.)
  useEffect(() => {
    window.addEventListener(OPEN_WHATS_NEW_EVENT, openDrawer);
    return () => window.removeEventListener(OPEN_WHATS_NEW_EVENT, openDrawer);
  }, [openDrawer]);

  const close = useCallback(() => {
    setOpen(false);
    localStorage.setItem(WHATS_NEW_SEEN_KEY, LATEST_VERSION);
  }, []);

  if (!open) return null;

  const release = RELEASES[0];
  const title = lang === "es" ? release.title_es : release.title_en;
  const highlights =
    lang === "es" ? release.highlights_es : release.highlights_en;

  const copy =
    lang === "es"
      ? {
          header: "Novedades",
          version_label: "Versión",
          close: "Cerrar",
          got_it: "Empezar",
        }
      : {
          header: "What's New",
          version_label: "Version",
          close: "Close",
          got_it: "Got it",
        };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-0 sm:p-6"
      data-testid="whats-new-drawer"
      role="dialog"
      aria-modal="true"
      aria-labelledby="whats-new-title"
    >
      {/* Backdrop */}
      <button
        onClick={close}
        aria-label={copy.close}
        className="absolute inset-0 bg-black/70 backdrop-blur-sm cursor-default"
        data-testid="whats-new-backdrop"
      />

      {/* Panel */}
      <div
        className="relative w-full sm:max-w-lg bg-[#121214] border border-[#F5A524]/25 sm:rounded-2xl rounded-t-3xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom-8 fade-in duration-300"
      >
        {/* Header */}
        <div className="relative px-5 sm:px-6 pt-5 pb-4 border-b border-white/5">
          <div className="flex items-start gap-3">
            <div className="shrink-0 w-10 h-10 rounded-xl bg-[#F5A524]/10 border border-[#F5A524]/25 flex items-center justify-center">
              <Sparkle weight="fill" className="w-5 h-5 text-[#F5A524]" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-[#F5A524] font-semibold mb-0.5">
                <span>{copy.header}</span>
                <span className="text-[#71717A] font-normal">·</span>
                <span className="text-[#A1A1AA] font-normal">
                  {copy.version_label} {release.version}
                </span>
                <span className="text-[#71717A] font-normal">·</span>
                <span className="text-[#A1A1AA] font-normal">
                  {release.date}
                </span>
              </div>
              <h2
                id="whats-new-title"
                className="font-['Outfit'] text-white text-lg sm:text-xl font-semibold leading-snug"
              >
                {title}
              </h2>
            </div>
            <button
              onClick={close}
              aria-label={copy.close}
              className="shrink-0 text-[#71717A] hover:text-white hover:bg-white/5 rounded-lg p-1.5 -m-1 transition-colors"
              data-testid="whats-new-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Highlights */}
        <ul className="px-5 sm:px-6 py-5 space-y-3 max-h-[55vh] overflow-y-auto">
          {highlights.map((h, i) => (
            <li
              key={i}
              className="flex gap-3 items-start"
              data-testid={`whats-new-item-${i}`}
            >
              <span
                className={`shrink-0 text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full border ${
                  h.tag === "New" || h.tag === "Nuevo"
                    ? "text-[#F5A524] border-[#F5A524]/30 bg-[#F5A524]/5"
                    : "text-emerald-400 border-emerald-400/30 bg-emerald-400/5"
                }`}
              >
                {h.tag}
              </span>
              <p className="text-[#D4D4D8] text-sm leading-relaxed flex-1">
                {h.text}
              </p>
            </li>
          ))}
        </ul>

        {/* Footer */}
        <div className="px-5 sm:px-6 py-4 border-t border-white/5 bg-[#0A0A0A]/40">
          <button
            onClick={close}
            className="w-full bg-[#F5A524] hover:bg-[#F5B041] text-[#0A0A0A] text-sm font-medium rounded-lg py-2.5 transition-colors"
            data-testid="whats-new-got-it"
          >
            {copy.got_it}
          </button>
        </div>
      </div>
    </div>
  );
}
